/**
 * OL-150: several PDFs can be sent for signature at once. They are combined
 * on the server into one packet, so fields are placed once and the signers
 * sign once. These helpers check the picked files and name the packet.
 */

export const MAX_PACKET_FILES = 20;

const MAX_NAME_LENGTH = 120;

const toMb = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;

/** Returns an error message for the picked files, or '' when they are fine. */
export function pdfSelectionError(files, maxBytes) {
  if (!files.length) return '';

  if (files.some((file) => file.type !== 'application/pdf')) {
    return 'Only PDF files are supported.';
  }

  const tooLarge = files.find((file) => file.size > maxBytes);
  if (tooLarge) {
    return `"${tooLarge.name}" is too large. Max allowed is ${toMb(maxBytes)}.`;
  }

  if (files.length > MAX_PACKET_FILES) {
    return `You can combine up to ${MAX_PACKET_FILES} PDFs at a time.`;
  }

  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (files.length > 1 && total > maxBytes) {
    return `Together these files are larger than ${toMb(maxBytes)}, the limit for one signing packet.`;
  }

  return '';
}

const COMBINE_FAILED = 'Could not combine the PDFs. Please try again.';

const blobText = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsText(blob);
});

/**
 * The server's message for a failed combine. The request asks for a PDF blob,
 * so a validation error arrives as a Blob holding the JSON body.
 */
export async function combineErrorMessage(errorResponse) {
  try {
    const raw = errorResponse?.data;
    const data = raw instanceof Blob ? JSON.parse(await blobText(raw)) : raw;
    const firstError = data?.errors ? Object.values(data.errors)[0] : null;

    return (Array.isArray(firstError) ? firstError[0] : firstError) || data?.message || COMBINE_FAILED;
  } catch {
    return COMBINE_FAILED;
  }
}

/**
 * Ghostscript can make the combined file bigger than its inputs. The send
 * endpoint rejects anything over the limit, so check before fields are placed.
 */
export function packetTooLargeError(packetBytes, maxBytes) {
  return packetBytes > maxBytes
    ? `The combined packet is ${toMb(packetBytes)}, over the ${toMb(maxBytes)} limit. Try fewer or smaller PDFs.`
    : '';
}

/** "Retainer + HIPAA Release.pdf", or "Retainer + 4 more.pdf" when that gets too long. */
export function packetName(files) {
  const names = files.map((file) => file.name.replace(/\.pdf$/i, ''));
  const joined = names.join(' + ');

  if (joined.length <= MAX_NAME_LENGTH) return `${joined}.pdf`;

  return `${names[0]} + ${names.length - 1} more.pdf`;
}
